import { useCurrentSite } from "../contexts/models/SiteContext";
import { isEmptySlateDocument } from "../core/constants";
import { richTextMarginBottomSpaceSwitch } from "../forms/components/RichText/utils";
import { Photo, RichTextType } from "../resources/modelTypes";
import { getPhotoByPk, RootState } from "../resources/reduxTypes";
import {
  ContentText,
  ContentWrapper,
  HeadlineSmall,
  HeadlineMedium,
  Image,
  RichTextContent,
} from "./components";
import { css } from "@emotion/react";
import { ActionLink } from "@minted/fancyclothes";
import React, { useMemo } from "react";
import { connect } from "react-redux";

export type ContentProps = {
  actionLinkProps?: {
    onClick: () => void;
    text: string;
  };
  configuration: {
    imageAlignment: string;
    imageSize: number;
    pageLayout: string;
    showMap: boolean;
    textAlignment: string;
  };
  description?: RichTextType | null;
  photoPk?: number | null;
  image?: string | null;
  title: string | null;
  url?: string;
};

const maxWidthImageSize = css`
  max-width: 650px;
  align-self: center;
`;

const richTextBottomMargin = (size: string) => css`
  margin-bottom: ${size};
`;

type ContentPropsWithRedux = {
  fullPhoto?: Photo | null;
} & ContentProps;

const Content = ({
  actionLinkProps,
  configuration,
  description,
  fullPhoto,
  image,
  title,
  url,
}: ContentPropsWithRedux) => {
  const site = useCurrentSite();
  const { isOnlineInvitation } = site;
  // OI fakes the section content to look like page content, so the header
  // needs to match the page header size

  const HeadlineComponent = isOnlineInvitation ? HeadlineMedium : HeadlineSmall;

  const imageToDisplay = useMemo(
    () => image || fullPhoto?.original || null,
    [fullPhoto?.original, image],
  );

  const richTextContentMb = richTextMarginBottomSpaceSwitch(
    configuration.imageAlignment,
    isEmptySlateDocument(description),
  );

  return (
    <ContentWrapper
      imageAlignment={configuration.imageAlignment}
      noVerticalMargin
      textAlignment={configuration.textAlignment}
    >
      <div css={maxWidthImageSize}>
        <Image
          image={imageToDisplay}
          imageAlignment={configuration.imageAlignment}
          imageSize={configuration.imageSize}
        />
      </div>
      <ContentText textAlignment={configuration.textAlignment}>
        {Boolean(title) && (
          <div data-cy="subheading">
            <HeadlineComponent imageAlignment={configuration.imageAlignment}>
              {title}
            </HeadlineComponent>
          </div>
        )}
        <div css={richTextBottomMargin(richTextContentMb)} data-cy="body">
          <RichTextContent value={description} />
          {url && actionLinkProps && (
            <ActionLink target="_blank" url={url} {...actionLinkProps} />
          )}
        </div>
      </ContentText>
    </ContentWrapper>
  );
};

const mapStateToProps = (state: RootState, props: ContentProps) => ({
  fullPhoto: getPhotoByPk(state, props.photoPk),
});

export default connect(mapStateToProps)(Content) as React.FC<ContentProps>;
